import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../../components/Theme";
import CreateChemicalStock from "../../api/stock/CreateChemicalStock";
import DeleteChemicalStock from "../../api/stock/DeleteChemicalStock";
import GetChemicalStock from "../../api/stock/GetChemicalStock";

export default function ChemicalStockTable({ appCropId }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [chemStocks, setChemStocks] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (appCropId) {
        try {
          const res = await GetChemicalStock({ appCropId });
          setChemStocks(res);
        } catch (err) {
          console.error("Error fetching chemical stock:", err);
        }
      }
    };
    fetchData();
  }, [appCropId]);

  const handleChange = (index, key, value) => {
    const updated = [...chemStocks];
    updated[index][key] = value;
    setChemStocks(updated);
  };

  const handleAdd = () => {
    const timestamp = Math.floor(Date.now() / 1000);
    setChemStocks([
      ...chemStocks,
      {
        appCropId: appCropId,
        appChemStockId: "SB" + timestamp,
        substance: "",
        commercialName: "",
        unitName: "กิโลกรัม",
        stockAmount: 0,
        stockDate: "",
      },
    ]);
  };

  const handleOpenDeleteDialog = (id) => {
    setSelectedDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await DeleteChemicalStock({ appChemStockId: selectedDeleteId });
      setChemStocks((prev) =>
        prev.filter((item) => item.appChemStockId !== selectedDeleteId)
      );
      setSnackbarSeverity("success");
      setSnackbarMessage("ลบรายการสำเร็จ");
    } catch (err) {
      setSnackbarSeverity("error");
      setSnackbarMessage("เกิดข้อผิดพลาดในการลบ");
    } finally {
      setDeleteDialogOpen(false);
      setOpenSnackbar(true);
    }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleSave = async () => {
    const results = await CreateChemicalStock(chemStocks);
    const hasError = results.some((r) => r.status !== 200);
    if (hasError) {
      setSnackbarSeverity("error");
      setSnackbarMessage("บางรายการบันทึกไม่สำเร็จ");
    } else {
      setSnackbarSeverity("success");
      setSnackbarMessage("บันทึกสำเร็จทั้งหมด");
    }
    setOpenSnackbar(true);
  };

  return (
    <Box p={2}>
      <Box
        width="100%"
        sx={{ backgroundColor: colors.greenAccent[300] }}
        display="flex"
        justifyContent="center"
      >
        <Typography
          p={isMobile ? 1 : 2}
          variant={isMobile ? "body1" : "h6"}
          fontWeight="bold"
          color="#fff"
        >
          สต็อกสารเคมี
        </Typography>
      </Box>

      <Box maxWidth={isMobile ? "320px" : "none"}>
        <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>วันที่</TableCell>
                <TableCell>สารออกฤทธิ์</TableCell>
                <TableCell>ชื่อทางการค้า</TableCell>
                <TableCell>จำนวน</TableCell>
                <TableCell align="center">ลบ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chemStocks.map((row, index) => (
                <TableRow key={row.appChemStockId || index}>
                  <TableCell>{index + 1}</TableCell>

                  <TableCell>
                    <TextField
                      type="date"
                      size="small"
                      value={row.stockDate}
                      onChange={(e) =>
                        handleChange(index, "stockDate", e.target.value)
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      sx={{ minWidth: "100px" }}
                      size="small"
                      value={row.substance}
                      onChange={(e) =>
                        handleChange(index, "substance", e.target.value)
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      sx={{ minWidth: "100px" }}
                      size="small"
                      value={row.commercialName}
                      onChange={(e) =>
                        handleChange(index, "commercialName", e.target.value)
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={row.stockAmount}
                      onChange={(e) =>
                        handleChange(index, "stockAmount", e.target.value)
                      }
                      InputProps={{
                        endAdornment: (
                          <span style={{ marginLeft: 4 }}>{row.unitName}</span>
                        ),
                      }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDeleteDialog(row.appChemStockId)}

                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    variant="outlined"
                  >
                    เพิ่มแถว
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box textAlign="center" mt={3}>
        <Button
          variant="contained"
          sx={{ backgroundColor: colors.greenAccent[400] }}
          onClick={handleSave}
        >
          บันทึกข้อมูล
        </Button>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            justifyContent: "center",
            minWidth: "200px",
            textAlign: "center",
          },
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Typography>คุณต้องการลบรายการนี้ใช่หรือไม่?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>ยกเลิก</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            ลบ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
